import "../../UserProfile/userProfile.scss";
import AuthClient from "../../../api/AuthClient";
import { iconsImgs } from "../../../utils/icons";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import Header from "../../Header";
import "./invitedFriendsInfo.scss"
import { Modal } from "react-bootstrap";
import { getUserAvatar } from "../../../utils/getMedia";
import { useLocation, useParams } from 'react-router-dom';
import { defaultImgs } from "../../../utils/defaultImages";
import RemoveGroupInvitationApi from "../../../api/RemoveGroupInvitationApi";
import { PartiesContext } from "../../../GlobalContext/partiesContext";
import handleImgError from "../../../handleImgError";
import ModalFriendPictureInput from "./ModalFriendPictureInput";
import { SelectedImageContext } from "../../../SelectedImageContext";
import GetSASTokenApi from "../../../api/GetSASTokenApi";
import { AzureBlob } from "../../../types";
import { uploadImageToStorage } from "../../../azureBlob/fileUpload";
import ReactiveButton from "reactive-button";
import { removeInvitationAndNavigate } from "./removeInvitationActions";
import normalizeGuestName from "../../../utils/normalizeGuestName";
import { localStorageKeys } from "../../../utils/localStorageKeys";
import AuthenticationResponse from "../../../api/responses/AuthenticationResponse";
import { Invitation, profileInfoUpdateRequestModel } from "../../../types";
import Gender from "../../../api/enums/Gender";
import EditUserProfileInfoApi from "../../../api/EditUserProfileInfoApi";
import { syncMyPartiesAndSelectedPartyFromGroupId } from "../../../utils/fetchAndSetMyPartiesInfo";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { validatePhoneNumberLength } from "libphonenumber-js";

const InvitedFriendInfo = () => {
    type RequestState = "idle" | "loading" | "success" | "error";

    const [showModal, setShowModal] = useState(false);
    const [isConfirmModalOpen, setConfirmationModalOpen] = useState(false);
    const [azureBlobsState, setAzureBlobsState] = useState<AzureBlob[] | null>(null);
    const [removeInvitationRequestState, setRemoveInvitationRequestState] = useState<RequestState>("idle");
    const [uploadPhotoRequestState, setUploadPhotoRequestState] = useState<RequestState>("idle");
    const [saveProfileRequestState, setSaveProfileRequestState] = useState<RequestState>("idle");
    const [isEditMode, setIsEditMode] = useState(false);
    const [saveProfileError, setSaveProfileError] = useState<string>("");
    const [phoneInputValue, setPhoneInputValue] = useState<string>("");

    useContext(SelectedImageContext);
    const { selectedParty, setMyPartiesInfoContextData, setSelectedPartyContextData } = useContext(PartiesContext);

    const handleCloseModal = () => setShowModal(false);
    const navigate = useNavigate();
    const params = useParams<{ groupId: string; friendId: string }>();

    const location = useLocation();
    const routeState = location.state as { friendInfo?: Invitation; groupId?: string } | null;
    const groupId = routeState?.groupId ?? params.groupId ?? "";
    const routeFriendId = params.friendId ?? routeState?.friendInfo?.id ?? "";
    const resolveGenderValue = (gender: unknown): Gender => {
        if (typeof gender === "number" && Gender[gender] !== undefined) {
            return gender as Gender;
        }

        if (typeof gender === "string") {
            const numericGender = Number(gender);
            if (!Number.isNaN(numericGender) && Gender[numericGender] !== undefined) {
                return numericGender as Gender;
            }

            const enumKey = Object.keys(Gender).find(
                (key) => Number.isNaN(Number(key)) && key.toLowerCase() === gender.toLowerCase()
            );
            if (enumKey && typeof Gender[enumKey as keyof typeof Gender] === "number") {
                return Gender[enumKey as keyof typeof Gender] as Gender;
            }
        }

        return Gender.Unkown;
    };

    const storedLoginResponse =
        sessionStorage.getItem(localStorageKeys.loginResponse) ??
        localStorage.getItem(localStorageKeys.loginResponse) ??
        "{}";
    const userInfo = JSON.parse(storedLoginResponse) as AuthenticationResponse;

    const selectedPartyInfo = useMemo(() => {
        if (selectedParty?.party.id !== groupId) {
            return undefined;
        }

        return selectedParty;
    }, [groupId, selectedParty]);

    const friendInfo = useMemo(() => {
        if (selectedPartyInfo && routeFriendId) {
            const invitationFromSelectedParty = selectedPartyInfo.invitations.find(
                (invitation) => invitation.id === routeFriendId
            );
            if (invitationFromSelectedParty) {
                return invitationFromSelectedParty;
            }
        }

        return routeState?.friendInfo;
    }, [routeFriendId, routeState?.friendInfo, selectedPartyInfo]);

    const [editableFriendInfo, setEditableFriendInfo] = useState<Invitation | undefined>(friendInfo);

    useEffect(() => {
        if (friendInfo) {
            setEditableFriendInfo(friendInfo);
        }
    }, [friendInfo]);

    useEffect(() => {
        if (!editableFriendInfo) {
            setPhoneInputValue("");
            return;
        }

        setPhoneInputValue(editableFriendInfo.phoneNumber ?? "");
    }, [editableFriendInfo?.phoneNumber]);

    const currentUserInvitation = useMemo(() => {
        return selectedPartyInfo?.invitations.find(
            (invitation) =>
                invitation.firstName === userInfo.firstName &&
                invitation.lastName === userInfo.lastName
        );
    }, [selectedPartyInfo, userInfo.firstName, userInfo.lastName]);

    const genderOptions = useMemo(
        () =>
            Object.values(Gender).filter(
                (genderValue): genderValue is Gender =>
                    typeof genderValue === "number" && genderValue !== Gender.Other
            ),
        []
    );

    if (!groupId || !friendInfo || !editableFriendInfo) {
        return (
            <>
                <Header />
                <div className="centerGroup">
                    <div className="desktopUI">
                        <p className="text-light">Invitation not found.</p>
                        <button className='BtnTop yellowTxt btn backBtn' onClick={() => navigate(-1)}>Back</button>
                    </div>
                </div>
            </>
        );
    }

    const isCurrentUserGroupAdmin = currentUserInvitation?.isGroupAdmin ?? false;
    const guestName = normalizeGuestName(editableFriendInfo?.firstName, editableFriendInfo?.lastName);
    const canEditProfile = isCurrentUserGroupAdmin;

    const removeInvitation = async () => {
        if (!isCurrentUserGroupAdmin || removeInvitationRequestState === "loading" || !friendInfo.id || !groupId) {
            return;
        }

        const authClient = new AuthClient();
        const api = new RemoveGroupInvitationApi(authClient);

        try {
            setRemoveInvitationRequestState("loading");
            await removeInvitationAndNavigate({
                groupId,
                invitedFriendId: friendInfo.id,
                removeInvitation: (currentGroupId, invitedFriendId) => api.RemoveInvitation(currentGroupId, invitedFriendId),
                setMyPartiesInfoContextData,
                setSelectedPartyContextData,
                closeConfirmationModal: () => setConfirmationModalOpen(false),
                navigate,
            });
            setRemoveInvitationRequestState("success");
        } catch (error) {
            console.error("Failed to remove invitation", error);
            setRemoveInvitationRequestState("error");
        }
    }

    const showFriendPhotoInputModal = () => {
        setUploadPhotoRequestState("idle");
        setAzureBlobsState(null);
        setShowModal(true)

        const authClient = new AuthClient();
        const getSasTokenApi = new GetSASTokenApi(authClient);

        getSasTokenApi.getSasToken(true, friendInfo.id, 0).then((sasToken) => {
            const azureBlob: AzureBlob = sasToken.data;
            setAzureBlobsState(Array.isArray(azureBlob) ? azureBlob : [azureBlob]);
        });
    }

    const updateEditableField = (
        field: keyof Invitation,
        value: string | number | undefined
    ) => {
        if (saveProfileRequestState !== "idle") {
            setSaveProfileRequestState("idle");
        }
        if (saveProfileError) {
            setSaveProfileError("");
        }

        setEditableFriendInfo((currentInfo) => {
            if (!currentInfo) {
                return currentInfo;
            }

            return {
                ...currentInfo,
                [field]: value,
            };
        });
    };

    const resolveDateOfBirth = () => {
        if (editableFriendInfo.dateOfBirth) {
            return new Date(editableFriendInfo.dateOfBirth);
        }

        const fallbackAge = Number.isFinite(editableFriendInfo.age)
            ? Math.max(18, editableFriendInfo.age)
            : 18;
        const fallbackDate = new Date();
        fallbackDate.setFullYear(fallbackDate.getFullYear() - fallbackAge);
        return fallbackDate;
    };

    const buildProfileUpdateModel = (updatedImgCount: number): profileInfoUpdateRequestModel => {
        const resolvedGender = resolveGenderValue(editableFriendInfo.gender);
        const firstName =
            editableFriendInfo.firstName?.trim() || Gender[resolvedGender] || "Guest";

        return {
            Id: editableFriendInfo.id,
            FirstName: firstName,
            LastName: editableFriendInfo.lastName?.trim() ?? "",
            DateOfBirth: resolveDateOfBirth(),
            Gender: resolvedGender,
            CityId: editableFriendInfo.cityId ?? userInfo.cityId,
            Email: editableFriendInfo.email?.trim() ?? "",
            PhoneNumber: phoneInputValue.trim(),
            UpdatedImgCount: updatedImgCount,
        };
    };

    const UploadImageToBlobStorage = async (file: File) => {
        if (!azureBlobsState || uploadPhotoRequestState === "loading") return;

        try {
            setUploadPhotoRequestState("loading");
            await uploadImageToStorage(file, azureBlobsState);

            // Blob upload alone does not update CountProfilePictures; admin uses that flag for guest avatars.
            const updatedImgCount = Math.max(1, editableFriendInfo.photos ?? 0);
            const authClient = new AuthClient();
            const editProfileApi = new EditUserProfileInfoApi(authClient);
            const response = await editProfileApi.editUserProfileInfo(
                buildProfileUpdateModel(updatedImgCount)
            );

            if (!response || response.statusCode >= 400) {
                throw new Error(response?.errorMessage ?? "Failed to update profile photo");
            }

            setEditableFriendInfo((currentInfo) =>
                currentInfo ? { ...currentInfo, photos: updatedImgCount } : currentInfo
            );

            try {
                await syncMyPartiesAndSelectedPartyFromGroupId(
                    authClient,
                    groupId,
                    setSelectedPartyContextData,
                    setMyPartiesInfoContextData
                );
            } catch (refreshError) {
                console.warn("Photo saved but failed to refresh party details.", refreshError);
            }

            setUploadPhotoRequestState("success");
            navigate(-1);
        } catch (error) {
            console.error("Failed to upload invited friend photo", error);
            setUploadPhotoRequestState("error");
        }
    };

    const saveProfile = async () => {
        if (!canEditProfile || saveProfileRequestState === "loading") {
            return;
        }

        setSaveProfileRequestState("idle");
        setSaveProfileError("");
        const firstName = editableFriendInfo.firstName?.trim();
        const lastName = editableFriendInfo.lastName?.trim() ?? "";
        const phoneNumber = phoneInputValue.trim();
        const email = editableFriendInfo.email?.trim() ?? "";

        if (!editableFriendInfo.id) {
            setSaveProfileRequestState("error");
            setSaveProfileError("Unable to save profile right now. Please try again.");
            return;
        }

        if (phoneNumber && !isValidPhoneNumber(phoneNumber)) {
            setSaveProfileRequestState("error");
            setSaveProfileError("Please enter a valid phone number for the selected country.");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (email && !emailRegex.test(email)) {
            setSaveProfileRequestState("error");
            setSaveProfileError("Please enter a valid email address.");
            return;
        }

        const resolvedGender = resolveGenderValue(editableFriendInfo.gender);
        const resolvedFirstName = firstName || Gender[resolvedGender] || "Guest";

        const authClient = new AuthClient();
        const editProfileApi = new EditUserProfileInfoApi(authClient);
        const model = buildProfileUpdateModel(editableFriendInfo.photos ?? 0);
        model.FirstName = resolvedFirstName;
        model.LastName = lastName;
        model.Email = email;
        model.PhoneNumber = phoneNumber;

        try {
            setSaveProfileRequestState("loading");
            const response = await editProfileApi.editUserProfileInfo(model);
            if (!response || response.statusCode >= 400) {
                throw new Error(response?.errorMessage ?? "Failed to edit profile");
            }

            setSaveProfileRequestState("success");
            setIsEditMode(false);
            setEditableFriendInfo((currentInfo) =>
                currentInfo
                    ? {
                        ...currentInfo,
                        firstName: resolvedFirstName,
                        lastName: lastName,
                        email: email,
                        phoneNumber: phoneNumber,
                        gender: resolvedGender,
                    }
                    : currentInfo
            );

            // Profile update succeeded. Refresh is best-effort only.
            try {
                await syncMyPartiesAndSelectedPartyFromGroupId(
                    authClient,
                    groupId,
                    setSelectedPartyContextData,
                    setMyPartiesInfoContextData
                );
            } catch (refreshError) {
                console.warn("Profile saved but failed to refresh party details.", refreshError);
            }
        } catch (error) {
            console.error("Failed to edit invited friend profile", error);
            setSaveProfileRequestState("error");
            setSaveProfileError(error instanceof Error && error.message
                ? error.message
                : "Unable to save profile right now. Please try again.");
        }
    };

    const cancelEditMode = () => {
        setEditableFriendInfo(friendInfo);
        setIsEditMode(false);
        setSaveProfileRequestState("idle");
        setSaveProfileError("");
    };

    return (
        <>
            <ModalFriendPictureInput showModal={showModal} handleCloseModal={handleCloseModal}
                UploadImageToBlobStorage={UploadImageToBlobStorage}
                isReady={azureBlobsState !== null}
                uploadRequestState={uploadPhotoRequestState} />

            <Modal
                backdrop={false}
                centered
                show={isConfirmModalOpen}
                onHide={() => {
                    if (removeInvitationRequestState !== "loading") {
                        setConfirmationModalOpen(false);
                    }
                }}
                keyboard={removeInvitationRequestState !== "loading"}
            >
                <Modal.Body>
                    Confirmation
                </Modal.Body>

                <Modal.Body className="text-center">
                    Are you sure you want to remove the invitation?
                </Modal.Body>

                <Modal.Footer>
                    <button className="btn btn-sm btn-outline-warning"
                        onClick={() => setConfirmationModalOpen(false)}
                        disabled={removeInvitationRequestState === "loading"}>
                        No
                    </button>
                    <ReactiveButton
                        buttonState={removeInvitationRequestState}
                        onClick={() => { void removeInvitation(); }}
                        color={'red'}
                        idleText={'Yes'}
                        loadingText={'Removing'}
                        successText={'Removed'}
                        errorText={'Error'}
                        type={'button'}
                        className="reactive-btn reactive-btn btn btn-sm btn-outline-danger red outline red"
                        outline={false}
                        shadow={false}
                        rounded={false}
                        size={'normal'}
                        block={false}
                        messageDuration={2000}
                        disabled={removeInvitationRequestState === "loading"}
                        buttonRef={null}
                        width={null}
                        height={null}
                        animation={true}
                    />
                </Modal.Footer>
            </Modal>
            <Header />
            <div className="centerGroup">
                <div className="desktopUI invited-friend-info-container">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                        <button className='BtnTop yellowTxt btn backBtn' onClick={() => navigate(-1)}>Back</button>
                        {canEditProfile && !isEditMode ? (
                            <button
                                className="btn btn-sm btn-outline-warning"
                                onClick={() => {
                                    setSaveProfileRequestState("idle");
                                    setSaveProfileError("");
                                    setIsEditMode(true);
                                }}
                            >
                                Edit Profile
                            </button>
                        ) : null}
                    </div>

                    <div className="pt-3 img-container pb-4">
                        <img
                            src={getUserAvatar(`${friendInfo.id}?${Date.now()}`) ?? defaultImgs.Avatar}
                            alt={`${userInfo.firstName ?? ""} ${userInfo.lastName ?? ""}`.trim()}
                            className="profileImage"
                            onError={(event) => { handleImgError(event, editableFriendInfo.gender.toString()) }}
                        />
                        {isCurrentUserGroupAdmin && (
                            <div className="d-flex justify-content-between w-100 text-light mt-2">
                                <button className="btn btn-sm btn-outline-warning" onClick={showFriendPhotoInputModal}>Change Photo</button>
                                <button className="btn btn-sm btn-outline-danger" onClick={() => setConfirmationModalOpen(true)}>Remove Invitation</button>
                            </div>
                        )}
                    </div>

                    <div className="d-flex my-1">
                        <img src={iconsImgs.User} className="mx-3 icons_" alt="icon" />
                        {isEditMode ? (
                            <div className="invited-friend-control d-flex gap-2">
                                <input
                                    type="text"
                                    className="form-control invited-friend-control-inner"
                                    value={editableFriendInfo.firstName ?? ""}
                                    onChange={(event) => updateEditableField("firstName", event.target.value)}
                                    placeholder="First name"
                                />
                                <input
                                    type="text"
                                    className="form-control invited-friend-control-inner"
                                    value={editableFriendInfo.lastName ?? ""}
                                    onChange={(event) => updateEditableField("lastName", event.target.value)}
                                    placeholder="Last name"
                                />
                            </div>
                        ) : (
                            <span className="text-light"> {guestName} </span>
                        )}
                    </div>
                    <div className="d-flex my-1">
                        <img src={iconsImgs.Mobile} className="mx-3 icons_" alt="icon" />
                        {isEditMode ? (
                            <div className="invited-friend-control">
                                <PhoneInput
                                    className="invited-friend-phone-input"
                                    defaultCountry="GB"
                                    value={phoneInputValue || undefined}
                                    onChange={(value) => {
                                        const nextValue = value ?? "";
                                        if (
                                            nextValue &&
                                            validatePhoneNumberLength(nextValue) === "TOO_LONG"
                                        ) {
                                            setSaveProfileError("Phone number is too long for the selected country.");
                                            return;
                                        }
                                        setPhoneInputValue(nextValue);
                                        updateEditableField("phoneNumber", nextValue);
                                    }}
                                    placeholder="Phone number"
                                />
                            </div>
                        ) : (
                            <span className="text-light">
                                {editableFriendInfo.phoneNumber
                                    ? editableFriendInfo.phoneNumber
                                    : "-"}
                            </span>
                        )}
                    </div>
                    <div className="d-flex my-1">
                        <img src={iconsImgs.Email} className="mx-3 icons_" alt="icon" />
                        {isEditMode ? (
                            <input
                                type="email"
                                className="form-control invited-friend-control"
                                value={editableFriendInfo.email ?? ""}
                                onChange={(event) => updateEditableField("email", event.target.value)}
                                placeholder="Email"
                            />
                        ) : (
                            <span className="text-light">{editableFriendInfo.email ?? "-"}</span>
                        )}
                    </div>
                    <div className="d-flex my-1">
                        <img src={iconsImgs.Genders} className="mx-3 icons_" alt="icon" />
                        {isEditMode ? (
                            <select
                                className="form-select invited-friend-control"
                                value={resolveGenderValue(editableFriendInfo.gender)}
                                onChange={(event) => updateEditableField("gender", Number(event.target.value))}
                            >
                                {genderOptions.map((genderOption) => (
                                    <option key={genderOption} value={genderOption}>
                                        {Gender[genderOption]}
                                    </option>
                                ))}
                            </select>
                        ) : (
                            <span className="text-light"> {Gender[resolveGenderValue(editableFriendInfo.gender)]} </span>
                        )}
                    </div>
                    {canEditProfile && isEditMode && (
                        <div className="d-flex justify-content-end mt-3 mb-4 gap-2">
                            <>
                                <button
                                    className="btn btn-sm btn-outline-light"
                                    onClick={cancelEditMode}
                                    disabled={saveProfileRequestState === "loading"}
                                >
                                    Cancel
                                </button>
                                <ReactiveButton
                                    buttonState={saveProfileRequestState}
                                    onClick={() => {
                                        void saveProfile();
                                    }}
                                    color={"yellow"}
                                    idleText={"Save Profile"}
                                    loadingText={"Saving"}
                                    successText={"Saved"}
                                    errorText={"Error"}
                                    type={"button"}
                                    className="btn btn-sm btn-outline-warning"
                                    outline={true}
                                    shadow={false}
                                    rounded={false}
                                    size={"small"}
                                    block={false}
                                    messageDuration={1500}
                                    disabled={saveProfileRequestState === "loading"}
                                    buttonRef={null}
                                    width={null}
                                    height={null}
                                    animation={true}
                                />
                            </>
                        </div>
                    )}
                    {isEditMode && saveProfileError ? (
                        <div className="text-danger mt-2">{saveProfileError}</div>
                    ) : null}
                </div>
            </div>
        </>
    );
};

export default InvitedFriendInfo;

